import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { Subscription, of, timer } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import {
  CustomerMigrationImportService,
  MigrationImportHistoryItem,
  MigrationHistoryPagination,
  MigrationJobProgressResponse,
  MigrationSourceType,
} from '../../services/customer-migration-import.service';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  VendorAdapterInfo,
  VendorCredentialInput,
  VendorCredentialStatus,
  VendorCredentialTestResult,
  VendorSyncJobProgress,
  VendorSyncService,
} from '../../services/vendor-sync.service';
import {
  VendorPlanMapping,
  VendorPlanMappingService,
} from '../../services/vendor-plan-mapping.service';
import { VendorModeService } from '../../../services/vendor-mode.service';
import {
  AdminLocationRow,
  LocationSettingsService,
} from '../../services/location-settings.service';

/**
 * A source is either one of the two file-based importers or a vendor adapter
 * name. Vendor modes are not enumerated here: they come from
 * `GET /vendor-sync/vendors` at runtime, so a backend that ships a new adapter
 * appears in this dropdown without a frontend release.
 */
export type ImportSourceMode = 'csv' | 'google_sheet' | string;

export interface ImportSourceOption {
  value: ImportSourceMode;
  label: string;
  hint: string;
  /** True for vendor-sync sources, false for the file-based importers. */
  isVendor: boolean;
}

/** The two sources that are always available, whatever adapters exist. */
export const STATIC_IMPORT_SOURCE_OPTIONS: readonly ImportSourceOption[] = [
  {
    value: 'csv',
    label: 'CSV upload',
    hint: 'Upload a prepared migration spreadsheet',
    isVendor: false,
  },
  {
    value: 'google_sheet',
    label: 'Google Sheet',
    hint: 'Import from a shared spreadsheet URL',
    isVendor: false,
  },
];

/**
 * Vendor adapters assumed present before `listVendors()` responds.
 *
 * Without this the source picker would briefly omit vendor options on every
 * load, and an operator mid-click would see the list move under them. Replaced
 * wholesale by the server's answer, so it is a rendering hint and never the
 * authority on what exists.
 */
export const FALLBACK_VENDOR_ADAPTERS: readonly VendorAdapterInfo[] = [
  {
    name: 'sitewatch',
    displayName: 'SiteWatch',
    credentialFields: [
      { key: 'employeeCode', label: 'Employee code', type: 'text', required: true, secret: true },
      { key: 'locationCode', label: 'Location code', type: 'text', required: true, secret: false },
      { key: 'password', label: 'Password', type: 'password', required: true, secret: true },
    ],
  },
];

/** Human-readable text for the reason codes vendorPlanMapping.service.js returns. */
const PLAN_MAPPING_INVALID_REASONS: Record<string, string> = {
  package_not_found: 'Package no longer exists',
  wrong_tenant: 'Package belongs to a different car wash',
  package_deleted: 'Package has been deleted',
  package_inactive: 'Package is inactive',
  not_car_wash_package: 'Only car wash packages can be mapped',
  not_monthly_membership: 'Only monthly membership packages can be mapped',
  period_day_not_supported: 'Daily packages cannot back a membership',
  monthly_with_zero_frequency_silent_deny: 'Monthly package has no wash allowance set',
  unrecognized_wash_count_period: 'Package configuration is not recognized',
};

/** BullMQ states that mean a vendor sync job has stopped. */
const VENDOR_TERMINAL_STATES = new Set(['completed', 'failed']);

/** Rows per page in the recent-imports table. The API caps a page at 50. */
const HISTORY_PAGE_SIZE = 10;

const MAX_CSV_BYTES = 25 * 1024 * 1024;
const TERMINAL_STATUSES = new Set(['completed', 'failed']);

const CLEAR_HISTORY_CONFIRMATION: ConfirmationModalData = {
  title: 'Clear recent imports?',
  description:
    'This removes completed and failed import history for this car wash. Active imports are kept. Result download links will no longer appear here.',
  cancelText: 'Cancel',
  confirmText: 'Clear history',
  showTimeInfo: false,
};

@Component({
  selector: 'app-import-data',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmationModalComponent],
  templateUrl: './import-data.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./import-data.component.scss'],
})
export class ImportDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) companyId!: string;
  /**
   * Hide the vendor sources, leaving only CSV and Google Sheet.
   *
   * Vendors are managed as systems on the Customer Migration tab — a bridge with
   * credentials, a schedule and a plan mapping is not an import source, and
   * offering it as one is what made its state invisible until selected. Set by
   * that tab when it embeds this component for the file-import path; unset
   * everywhere else, so any remaining caller behaves exactly as before.
   */
  @Input() fileOnly = false;

  sourceMode: ImportSourceMode = 'csv';
  selectedFile: File | null = null;
  isDragOver = false;
  googleSheetUrl = '';
  fieldError = '';
  isSubmitting = false;
  isDownloadingTemplate = false;

  activeJobId: string | null = null;
  jobStatus: string | null = null;
  jobProgress: MigrationJobProgressResponse['progress'] | null = null;
  jobFailedReason: string | null = null;
  jobSourceType: MigrationSourceType | null = null;
  history: MigrationImportHistoryItem[] = [];
  historyLoading = false;
  /** Paging for the recent-imports table. 1-based, to match the API. */
  historyPage = 1;
  historyPagination: MigrationHistoryPagination | null = null;
  isClearingHistory = false;
  showClearHistoryConfirmation = false;
  clearHistoryConfirmationData: ConfirmationModalData = { ...CLEAR_HISTORY_CONFIRMATION };

  // ── Vendor sync ────────────────────────────────────────────────────────────
  /** Adapters the backend supports, replaced by listVendors() on init. */
  vendorAdapters: VendorAdapterInfo[] = [...FALLBACK_VENDOR_ADAPTERS];
  /**
   * Credential form values, keyed by the active adapter's field keys.
   *
   * One bag rather than a property per field: the field set belongs to the
   * adapter, so hardcoding properties here is what made adding a vendor a UI
   * change. Cleared after save so a password never lingers in component state.
   */
  credentialValues: Record<string, string> = {};
  /**
   * Whether the credential form is expanded. Collapsed once credentials are
   * stored, so returning to the page shows a connected summary and a sync
   * button rather than an empty form implying re-entry is required.
   */
  showCredentialForm = false;
  credentialStatus: VendorCredentialStatus | null = null;
  credentialStatusLoading = false;
  isTestingCredentials = false;
  isSavingCredentials = false;
  isTogglingSyncEnabled = false;
  credentialTestResult: VendorCredentialTestResult | null = null;
  credentialError = '';
  vendorJobId: string | null = null;
  vendorJobState: string | null = null;
  vendorJobProgress: VendorSyncJobProgress | null = null;
  vendorJobFailedReason: string | null = null;

  // ── Vendor plan → package mapping (CWS-734 Phase 2) ─────────────────────────
  planMapping: VendorPlanMapping | null = null;
  planMappingLoading = false;
  planMappingError = '';
  planMappingSaved = false;
  isSavingPlanMapping = false;
  /** Editable draft, seeded from planMapping on load/save; not synced back until Save is clicked. */
  planMappingRows: { planName: string; packageId: string; valid: boolean; reason?: string }[] = [];
  newPlanName = '';
  newPackageId = '';

  // ── Lane redeem controls (CWS-920) ──────────────────────────────────────────
  // Separate from the sync toggle above: these govern the gate, not the nightly
  // pull, and stay in force while the sync is paused.
  isSavingPlateMatching = false;
  plateMatchingError = '';
  /** Locations for the allowlist picker, loaded lazily when the vendor section renders. */
  shopLocations: AdminLocationRow[] = [];
  shopLocationsLoading = false;

  private readonly importService = inject(CustomerMigrationImportService);
  private readonly vendorSyncService = inject(VendorSyncService);
  private readonly locationSettingsService = inject(LocationSettingsService);
  private readonly vendorPlanMappingService = inject(VendorPlanMappingService);
  private readonly vendorModeService = inject(VendorModeService);
  private readonly messageService = inject(NotificationService);
  private pollSub: Subscription | null = null;
  private historySub: Subscription | null = null;
  private credentialSub: Subscription | null = null;
  private vendorPollSub: Subscription | null = null;
  private planMappingSub: Subscription | null = null;
  private shopLocationsSub: Subscription | null = null;

  ngOnInit(): void {
    this.loadVendorAdapters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && !changes['companyId'].firstChange) {
      this.resetForm({ keepMode: true });
    }
    if (changes['companyId']?.currentValue) {
      this.loadHistory();
      // Credential state, plan mapping and the location list are all per company,
      // so they must be refetched on switch.
      if (this.isVendorMode) {
        this.loadCredentialStatus();
        this.loadPlanMapping();
        this.shopLocations = [];
        this.loadShopLocations();
      }
    } else if (changes['companyId']) {
      this.stopHistoryLoad();
      this.stopCredentialLoad();
      this.stopPlanMappingLoad();
      this.stopShopLocationsLoad();
      this.history = [];
      this.historyLoading = false;
      this.historyPage = 1;
      this.credentialStatus = null;
      this.planMapping = null;
      this.planMappingRows = [];
      this.shopLocations = [];
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopHistoryLoad();
    this.stopCredentialLoad();
    this.stopVendorPolling();
    this.stopPlanMappingLoad();
    this.stopShopLocationsLoad();
  }

  get canSubmit(): boolean {
    if (this.isSubmitting || this.isJobRunning) {
      return false;
    }
    if (this.sourceMode === 'csv') {
      return Boolean(this.selectedFile);
    }
    if (this.isVendorMode) {
      // Credentials are resolved server-side, so a sync can only start once
      // they are stored. Without this the request would fail with an
      // unhelpful error after the user had already committed. A paused sync is
      // rejected server-side too (409) — this just avoids offering the button.
      return (
        Boolean(this.credentialStatus?.configured) &&
        this.credentialStatus?.enabled !== false &&
        !this.credentialStatusLoading
      );
    }
    return Boolean(this.googleSheetUrl.trim());
  }

  get isJobRunning(): boolean {
    if (this.isVendorJobRunning) {
      return true;
    }
    return Boolean(this.activeJobId && this.jobStatus && !TERMINAL_STATUSES.has(this.jobStatus));
  }

  /** True when this shop's vendor sync is paused — credentials stored, syncing off. */
  get isSyncPaused(): boolean {
    return Boolean(this.credentialStatus?.configured) && this.credentialStatus?.enabled === false;
  }

  /**
   * Pause or resume the sync. Non-destructive: credentials and the plan mapping
   * are kept, so resuming needs no re-setup.
   */
  onToggleSyncEnabled(): void {
    const vendor = this.activeVendor;
    if (!vendor || !this.companyId || this.isTogglingSyncEnabled || this.isJobRunning) {
      return;
    }
    const next = !this.credentialStatus?.enabled;
    const label = this.activeVendorLabel;
    this.isTogglingSyncEnabled = true;
    this.credentialError = '';

    this.vendorSyncService.setSyncEnabled(vendor, this.companyId, next).subscribe({
      next: response => {
        this.isTogglingSyncEnabled = false;
        // Render from the server's status rather than assuming the write landed.
        this.credentialStatus = response.data ?? this.credentialStatus;
        // Vendor UI elsewhere in this app reads cached flags — re-read them so the
        // change shows without a reload. Other browser sessions still need one.
        this.vendorModeService.refresh();
        this.messageService.add({
          severity: 'success',
          summary: next ? 'Sync enabled' : 'Sync disabled',
          detail: next
            ? `${label} will sync on the nightly schedule again.`
            : `${label} will not sync. Imported customers and their memberships are unaffected.`,
        });
      },
      error: err => {
        this.isTogglingSyncEnabled = false;
        this.credentialError = this.describeVendorError(err, 'Failed to update sync state');
      },
    });
  }

  // ── Lane redeem controls (CWS-920) ──────────────────────────────────────────

  /**
   * Resolved settings, or the server-side defaults while the status is still loading
   * or when talking to a svc-vendor-sync that predates the field.
   *
   * Must mirror libs/core vendorPlateMatching: auto-redeem and Collect payment
   * are opt-in (off until a super admin turns them on), the location allowlist
   * is opt-out (empty = every location). Defaulting either flag to true here
   * would show a shop as enabled when the gate is not.
   */
  get plateMatching(): { autoRedeem: boolean; collectPayment: boolean; locationIds: string[] } {
    return (
      this.credentialStatus?.plateMatching ?? {
        autoRedeem: false,
        collectPayment: false,
        locationIds: [],
      }
    );
  }

  /** Empty allowlist means every location, which is the pre-feature behaviour. */
  get isPlateMatchingLocationRestricted(): boolean {
    return this.plateMatching.locationIds.length > 0;
  }

  isPlateMatchingLocationSelected(locationId: string): boolean {
    return this.plateMatching.locationIds.includes(String(locationId).toLowerCase());
  }

  /** Branch names for the current allowlist, for a one-line summary. */
  get selectedPlateMatchingLocationNames(): string[] {
    const selected = new Set(this.plateMatching.locationIds);
    return this.shopLocations
      .filter(l => selected.has(String(l.id).toLowerCase()))
      .map(l => l.branch || l.name || l.id);
  }

  /**
   * Locations are only needed once the vendor section is on screen, and only a
   * super admin can reach this page — so this is loaded alongside the credential
   * status rather than with the component.
   */
  loadShopLocations(): void {
    // Drop any request still in flight for the previous company rather than
    // skipping this one: the caller has already cleared the list, so refusing to
    // reload would leave the picker permanently empty for the new company.
    this.stopShopLocationsLoad();
    if (!this.companyId) {
      return;
    }
    const companyId = this.companyId;
    this.shopLocationsLoading = true;
    this.shopLocationsSub = this.locationSettingsService.listLocations(companyId).subscribe({
      next: response => {
        // Guard against a stale response after the operator switched company.
        if (companyId !== this.companyId) return;
        this.shopLocationsSub = null;
        this.shopLocationsLoading = false;
        this.shopLocations = Array.isArray(response.data) ? response.data : [];
      },
      error: () => {
        if (companyId !== this.companyId) return;
        this.shopLocationsSub = null;
        this.shopLocationsLoading = false;
        // Non-fatal: the toggle still works, the picker just cannot be rendered.
        this.shopLocations = [];
      },
    });
  }

  private stopShopLocationsLoad(): void {
    if (this.shopLocationsSub) {
      this.shopLocationsSub.unsubscribe();
      this.shopLocationsSub = null;
    }
    this.shopLocationsLoading = false;
  }

  onToggleVendorAutoRedeem(): void {
    this.savePlateMatching({ autoRedeem: !this.plateMatching.autoRedeem });
  }

  onToggleVendorCollectPayment(): void {
    this.savePlateMatching({ collectPayment: !this.plateMatching.collectPayment });
  }

  onTogglePlateMatchingLocation(locationId: string): void {
    const id = String(locationId).toLowerCase();
    const current = this.plateMatching.locationIds;
    const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    this.savePlateMatching({ locationIds: next });
  }

  /** Clear the restriction — back to "every location", the default. */
  onClearPlateMatchingLocations(): void {
    if (!this.isPlateMatchingLocationRestricted) return;
    this.savePlateMatching({ locationIds: [] });
  }

  private savePlateMatching(patch: {
    autoRedeem?: boolean;
    collectPayment?: boolean;
    locationIds?: string[];
  }): void {
    const vendor = this.activeVendor;
    if (!vendor || !this.companyId || this.isSavingPlateMatching) {
      return;
    }
    const companyId = this.companyId;
    const label = this.activeVendorLabel;
    this.isSavingPlateMatching = true;
    this.plateMatchingError = '';

    this.vendorSyncService.setPlateMatching(vendor, companyId, patch).subscribe({
      next: response => {
        // Always released, even for a response the guard below discards, or the
        // section would stay locked against further edits.
        this.isSavingPlateMatching = false;
        // The settings belong to the (company, vendor) that was saved. Once the
        // operator has switched away, applying them would show another
        // integration's redeem controls and confirm a save they cannot see.
        if (companyId !== this.companyId || vendor !== this.activeVendor) return;
        // Render from the server's resolved values — it, not this component, decides
        // what an empty allowlist or an absent flag means.
        this.credentialStatus = response.data ?? this.credentialStatus;
        this.messageService.add({
          severity: 'success',
          summary: 'Redeem settings saved',
          detail: this.describePlateMatching(label),
        });
      },
      error: err => {
        this.isSavingPlateMatching = false;
        if (companyId !== this.companyId || vendor !== this.activeVendor) return;
        this.plateMatchingError = this.describeVendorError(err, 'Failed to save redeem settings');
      },
    });
  }

  /** Plain-language summary of what the gate will now do, for the save toast. */
  private describePlateMatching(label: string): string {
    const { autoRedeem, collectPayment, locationIds } = this.plateMatching;
    const where =
      locationIds.length === 0
        ? 'at every location'
        : `at ${locationIds.length} of ${this.shopLocations.length || locationIds.length} locations`;
    const redeem = autoRedeem
      ? `${label} memberships auto-redeem on a plate read ${where}`
      : `${label} plate reads now ask an attendant instead of redeeming automatically (${where})`;
    const pay = collectPayment
      ? 'Collect payment is shown on Active subscription.'
      : 'Collect payment is hidden on Active subscription.';
    return `${redeem}. ${pay}`;
  }

  get isVendorJobRunning(): boolean {
    return Boolean(
      this.vendorJobId && this.vendorJobState && !VENDOR_TERMINAL_STATES.has(this.vendorJobState)
    );
  }

  /**
   * Credentials can only be saved when the encryption key is usable — without it
   * the save would fail server-side at encryption time.
   */
  get canEditCredentials(): boolean {
    return this.credentialStatus?.encryptionConfigured !== false;
  }

  /**
   * The vendor adapter behind the current source, or null for csv/google_sheet.
   *
   * Everything vendor-related keys off this rather than a hardcoded constant, so
   * the same code paths drive every adapter.
   */
  get activeVendor(): string | null {
    return this.isVendorMode ? this.sourceMode : null;
  }

  /** True when the selected source is a vendor sync rather than a file import. */
  get isVendorMode(): boolean {
    if (this.fileOnly) return false;
    return this.vendorAdapters.some(adapter => adapter.name === this.sourceMode);
  }

  get activeAdapter(): VendorAdapterInfo | null {
    return this.vendorAdapters.find(adapter => adapter.name === this.sourceMode) ?? null;
  }

  /** Vendor name for user-facing copy; falls back to the raw key. */
  get activeVendorLabel(): string {
    return this.activeAdapter?.displayName ?? this.activeVendor ?? 'the vendor';
  }

  get activeCredentialFields(): VendorAdapterInfo['credentialFields'] {
    return this.activeAdapter?.credentialFields ?? [];
  }

  /** Every field the active adapter marks required must be filled to test or save. */
  get hasCredentialInput(): boolean {
    const fields = this.activeCredentialFields.filter(field => field.required);
    if (fields.length === 0) return false;
    return fields.every(field => (this.credentialValues[field.key] ?? '').trim().length > 0);
  }

  /**
   * Source dropdown contents: the two file importers plus one entry per adapter
   * the backend reports.
   */
  get sourceOptions(): ImportSourceOption[] {
    if (this.fileOnly) {
      return [...STATIC_IMPORT_SOURCE_OPTIONS];
    }
    return [
      ...STATIC_IMPORT_SOURCE_OPTIONS,
      ...this.vendorAdapters.map(adapter => ({
        value: adapter.name,
        label: adapter.displayName,
        hint: `Pull members directly from ${adapter.displayName}`,
        isVendor: true,
      })),
    ];
  }

  /** Hint shown under the source dropdown for the current selection. */
  get selectedSourceHint(): string {
    return this.sourceOptions.find(o => o.value === this.sourceMode)?.hint ?? '';
  }

  /**
   * "Before you import" notes for the selected vendor.
   *
   * The caveats genuinely differ per source — SiteWatch drops members with no
   * contact details and carries cancelled plans as inactive, EverWash imports
   * only the active roster and has no vehicle data at all — so a single generic
   * list would be wrong for both. Unknown vendors get the shared facts only.
   */
  get vendorImportNotes(): string[] {
    const shared = [
      'Runs through the same importer, so existing customers are updated',
      'Card details are never copied from the vendor',
    ];
    switch (this.activeVendor) {
      case 'sitewatch':
        return [
          'Pulls the Member Analysis pass report for this location',
          'Members without an email, phone or plate cannot be matched and are skipped',
          'Cancelled plans import as inactive customers',
          ...shared,
        ];
      case 'everwash':
        return [
          'Pulls the active membership roster for this location',
          'Lapsed and cancelled members are not imported',
          'A member with several vehicles imports as one customer',
          'EverWash holds no vehicle make, model or year — only the plate',
          ...shared,
        ];
      default:
        return shared;
    }
  }

  /**
   * Load the adapter list so the source picker and credential form match what
   * the backend actually supports.
   *
   * Failure is deliberately quiet: the fallback list already renders SiteWatch,
   * and an operator who only wants a CSV upload should not be shown an error
   * about a service they are not using. A vendor sync they then attempt will
   * surface the outage through describeVendorError.
   */
  private loadVendorAdapters(): void {
    this.vendorSyncService.listVendors().subscribe({
      next: response => {
        const adapters = response.data?.adapters;
        if (adapters?.length) {
          this.vendorAdapters = adapters;
        }
      },
      error: () => {
        /* keep the fallback list */
      },
    });
  }

  /** Expand/collapse the credential form when credentials are already stored. */
  onToggleCredentialForm(): void {
    this.showCredentialForm = !this.showCredentialForm;
    this.credentialError = '';
    if (!this.showCredentialForm) {
      this.clearCredentialInputs();
    }
  }

  onDeleteCredentials(): void {
    const vendor = this.activeVendor;
    if (!vendor || !this.companyId || this.isSavingCredentials || this.isJobRunning) {
      return;
    }
    const label = this.activeVendorLabel;
    this.isSavingCredentials = true;
    this.credentialError = '';
    this.vendorSyncService.deleteCredentials(vendor, this.companyId).subscribe({
      next: () => {
        this.isSavingCredentials = false;
        this.clearCredentialInputs();
        this.messageService.add({
          severity: 'success',
          summary: 'Credentials removed',
          detail: `${label} credentials deleted for this car wash.`,
        });
        this.loadCredentialStatus();
        this.loadPlanMapping();
      },
      error: err => {
        this.isSavingCredentials = false;
        this.credentialError = this.describeVendorError(err, 'Failed to remove credentials');
      },
    });
  }

  get vendorJobStateLabel(): string {
    if (!this.vendorJobState) return 'Queued';
    if (this.vendorJobState === 'active' || this.vendorJobState === 'waiting') {
      return 'In progress';
    }
    return this.vendorJobState.charAt(0).toUpperCase() + this.vendorJobState.slice(1);
  }

  /**
   * Members fetched from the vendor but not sent to the importer, because they
   * had no email, phone or plate to match on. Surfaced rather than hidden — it
   * is a source-data signal, not an error.
   */
  get vendorSkippedCount(): number {
    return this.vendorJobProgress?.skipped ?? 0;
  }

  /** Members the vendor reports holding, known once the first page arrives. */
  get vendorReportTotal(): number | null {
    return this.vendorJobProgress?.reportTotal ?? null;
  }

  /**
   * True while the first batch of members is still being fetched, so zeros mean
   * "waiting", not "nothing found" — worth saying explicitly rather than showing
   * a row of zeroes that looks stalled.
   */
  get isAwaitingFirstVendorPage(): boolean {
    return this.isVendorJobRunning && (this.vendorJobProgress?.fetched ?? 0) === 0;
  }

  /**
   * Why the counts are still zero. The reason is genuinely vendor-specific:
   * SiteWatch queues an async report that blocks ~30s per page, while EverWash
   * walks its wash log to resolve plates before the first row can be built.
   */
  get vendorWaitingMessage(): string {
    switch (this.activeVendor) {
      case 'sitewatch':
        return 'Waiting for SiteWatch to generate the first report page — this usually takes about 30 seconds. Counts below start moving once it arrives.';
      case 'everwash':
        return 'Reading the EverWash roster and matching plates to members. Counts below start moving once that finishes.';
      default:
        return `Waiting for ${this.activeVendorLabel} to return the first members. Counts below start moving once they arrive.`;
    }
  }

  /**
   * Why members were held back. SiteWatch drops anyone with no email, phone or
   * plate; EverWash only drops rows with no name, since its roster always has
   * one — so a shared message would be wrong for whichever vendor it did not
   * describe.
   */
  get vendorSkippedMessage(): string {
    const n = this.vendorSkippedCount;
    const one = n === 1;
    const subject = `${n} member${one ? '' : 's'} ${one ? 'was' : 'were'} skipped`;
    switch (this.activeVendor) {
      case 'sitewatch':
        return `${subject} because SiteWatch held no email, phone or licence plate for them — the importer cannot match a customer without one.`;
      case 'everwash':
        return `${subject} because EverWash held no name for them — the importer cannot create a customer without one.`;
      default:
        return `${subject} because they could not be matched to a customer.`;
    }
  }

  get progressPercent(): number {
    const percent = this.jobProgress?.percent;
    const normalizedPercent =
      typeof percent === 'number' && Number.isFinite(percent)
        ? Math.max(0, Math.min(100, Math.round(percent)))
        : 0;

    // Product requirement for CSV imports: show a simple staged progress UX
    // (0 -> 50 while processing -> 100 on completion) instead of granular jumps.
    if (this.jobSourceType === 'csv') {
      if (this.jobStatus === 'completed') {
        return 100;
      }
      if (this.jobStatus === 'failed') {
        return normalizedPercent;
      }
      if (
        this.jobStatus === 'active' ||
        this.jobStatus === 'waiting' ||
        this.jobStatus === 'queued'
      ) {
        return 50;
      }
      return 0;
    }

    if (typeof percent === 'number' && Number.isFinite(percent)) {
      return normalizedPercent;
    }
    return 0;
  }

  get statusLabel(): string {
    if (!this.jobStatus) {
      return 'Queued';
    }
    if (this.jobStatus === 'active' || this.jobStatus === 'waiting') {
      return 'In progress';
    }
    if (this.jobStatus === 'completed') {
      return 'Completed';
    }
    if (this.jobStatus === 'failed') {
      return 'Failed';
    }
    return this.jobStatus.charAt(0).toUpperCase() + this.jobStatus.slice(1);
  }

  get statusTone(): 'idle' | 'running' | 'success' | 'danger' {
    if (this.jobStatus === 'completed') {
      return 'success';
    }
    if (this.jobStatus === 'failed') {
      return 'danger';
    }
    if (this.isJobRunning || this.jobStatus === 'queued' || this.jobStatus === 'waiting') {
      return 'running';
    }
    return 'idle';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatHistoryDate(value?: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  }

  getHistoryStatusLabel(status?: string): string {
    if (!status) return 'Unknown';
    if (status === 'processing') return 'In progress';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  /**
   * Vendor syncs land in history as `csv` because the vendor service hands a
   * generated CSV to the same importer, so the file name is what distinguishes
   * them. svc-vendor-sync names those files `<vendor>-...`, so the prefix is
   * matched against the known adapters rather than one hardcoded vendor.
   */
  getHistorySourceLabel(item: MigrationImportHistoryItem): string {
    if (item.source_type === 'google_sheet') return 'Google Sheet';
    const fileName = item.source_file_name ?? '';
    const adapter = this.vendorAdapters.find(a => fileName.startsWith(`${a.name}-`));
    if (adapter) return adapter.displayName;
    return 'CSV';
  }

  selectSourceMode(mode: ImportSourceMode): void {
    if (this.isSubmitting || this.isJobRunning || this.sourceMode === mode) {
      return;
    }
    this.sourceMode = mode;
    this.fieldError = '';
    this.selectedFile = null;
    // Credential state and plan mapping are per (vendor, company), so switching
    // between two vendors must refetch both rather than reuse the previous
    // vendor's answers.
    this.credentialStatus = null;
    this.credentialTestResult = null;
    this.clearCredentialInputs();
    if (this.isVendorMode) {
      this.loadCredentialStatus();
      this.loadPlanMapping();
      // Backs the redeem-location picker.
      this.loadShopLocations();
    }
  }

  /**
   * Fetch whether credentials exist for this company. The response carries no
   * credential values — only booleans and the non-secret location code.
   */
  loadCredentialStatus(): void {
    this.stopCredentialLoad();
    const vendor = this.activeVendor;
    if (!vendor || !this.companyId) {
      this.credentialStatus = null;
      return;
    }
    const companyId = this.companyId;
    const label = this.activeVendorLabel;
    this.credentialStatusLoading = true;
    this.credentialError = '';
    this.credentialSub = this.vendorSyncService.getCredentialStatus(vendor, companyId).subscribe({
      next: response => {
        // A slow response for a company or vendor the operator has already
        // switched away from must not overwrite the current view.
        if (companyId !== this.companyId || vendor !== this.activeVendor) return;
        this.credentialStatusLoading = false;
        this.credentialSub = null;
        this.credentialStatus = response.data ?? null;
        // Already connected → collapse the form; not connected → the operator
        // has to enter credentials, so open it for them.
        this.showCredentialForm = !this.credentialStatus?.configured;
      },
      error: err => {
        if (companyId !== this.companyId || vendor !== this.activeVendor) return;
        this.credentialStatusLoading = false;
        this.credentialSub = null;
        this.credentialStatus = null;
        this.credentialError = this.describeVendorError(
          err,
          `Failed to load ${label} connection status`
        );
      },
    });
  }

  /**
   * Fetch the current plan → package mapping plus this shop's packages to pick
   * from. Works even without stored credentials (has_credentials will be
   * false) — a mapping can be built ahead of time, it just can't be saved yet.
   */
  loadPlanMapping(): void {
    this.stopPlanMappingLoad();
    const vendor = this.activeVendor;
    if (!vendor || !this.companyId) {
      this.planMapping = null;
      this.planMappingRows = [];
      return;
    }
    const companyId = this.companyId;
    this.planMappingLoading = true;
    this.planMappingError = '';
    this.planMappingSaved = false;
    this.planMappingSub = this.vendorPlanMappingService
      .getPlanMapping(companyId, vendor)
      .subscribe({
        next: response => {
          if (companyId !== this.companyId) return;
          this.planMappingLoading = false;
          this.planMappingSub = null;
          this.planMapping = response.data ?? null;
          this.planMappingRows = (this.planMapping?.plan_package_map ?? []).map(entry => ({
            planName: entry.plan_name,
            packageId: entry.package_id,
            valid: entry.valid,
            reason: entry.reason,
          }));
        },
        error: err => {
          if (companyId !== this.companyId) return;
          this.planMappingLoading = false;
          this.planMappingSub = null;
          this.planMapping = null;
          this.planMappingRows = [];
          this.planMappingError = this.describeVendorError(err, 'Failed to load plan mapping');
        },
      });
  }

  /** Human-readable text for a mapped-package's invalid reason code. */
  describePlanMappingReason(reason?: string): string {
    if (!reason) return 'This package is no longer valid';
    return PLAN_MAPPING_INVALID_REASONS[reason] ?? 'This package is no longer valid';
  }

  /**
   * Add or update a draft row. Re-typing an existing plan name updates its
   * package in place rather than creating a second row for the same plan —
   * the backend rejects a duplicate plan name outright (savePlanPackageMap).
   */
  onAddPlanMappingRow(): void {
    const planName = this.newPlanName.trim();
    if (!planName || !this.newPackageId) {
      return;
    }
    const existing = this.planMappingRows.find(
      row => row.planName.toLowerCase() === planName.toLowerCase()
    );
    if (existing) {
      existing.packageId = this.newPackageId;
      existing.valid = true;
      existing.reason = undefined;
    } else {
      this.planMappingRows = [
        ...this.planMappingRows,
        { planName, packageId: this.newPackageId, valid: true },
      ];
    }
    this.newPlanName = '';
    this.newPackageId = '';
    this.planMappingSaved = false;
  }

  onRemovePlanMappingRow(index: number): void {
    this.planMappingRows = this.planMappingRows.filter((_, i) => i !== index);
    this.planMappingSaved = false;
  }

  /** Draft row's package changed via its own dropdown — clear its stale validity badge. */
  onPlanMappingRowPackageChange(row: { valid: boolean; reason?: string }): void {
    row.valid = true;
    row.reason = undefined;
    this.planMappingSaved = false;
  }

  /**
   * Save replaces the *entire* mapping (all-or-nothing on the backend) — an
   * empty draft therefore clears every existing mapping, not a no-op, so a
   * confirmation-free "Save" with zero rows is intentional here: the operator
   * removed each row explicitly via its own Remove button first.
   */
  onSavePlanMapping(): void {
    const vendor = this.activeVendor;
    if (
      !vendor ||
      !this.companyId ||
      this.isSavingPlanMapping ||
      !this.planMapping?.has_credentials
    ) {
      return;
    }
    const label = this.activeVendorLabel;
    this.isSavingPlanMapping = true;
    this.planMappingError = '';
    this.planMappingSaved = false;

    const planPackageMap: Record<string, string> = {};
    for (const row of this.planMappingRows) {
      if (row.planName.trim() && row.packageId) {
        planPackageMap[row.planName.trim()] = row.packageId;
      }
    }

    this.vendorPlanMappingService
      .updatePlanMapping(this.companyId, vendor, planPackageMap)
      .subscribe({
        next: response => {
          this.isSavingPlanMapping = false;
          this.planMapping = response.data ?? this.planMapping;
          this.planMappingRows = (this.planMapping?.plan_package_map ?? []).map(entry => ({
            planName: entry.plan_name,
            packageId: entry.package_id,
            valid: entry.valid,
            reason: entry.reason,
          }));
          this.planMappingSaved = true;
          this.messageService.add({
            severity: 'success',
            summary: 'Plan mapping saved',
            detail: `${label} plans will resolve to these packages on the next sync.`,
          });
        },
        error: err => {
          this.isSavingPlanMapping = false;
          const errors = err?.error?.data?.errors as Record<string, string> | undefined;
          if (errors && Object.keys(errors).length) {
            const details = Object.entries(errors)
              .map(([planName, reason]) => `${planName}: ${this.describePlanMappingReason(reason)}`)
              .join('; ');
            this.planMappingError = `Some plans could not be saved — ${details}`;
          } else {
            this.planMappingError = this.describeVendorError(err, 'Failed to save plan mapping');
          }
        },
      });
  }

  private stopPlanMappingLoad(): void {
    if (this.planMappingSub) {
      this.planMappingSub.unsubscribe();
      this.planMappingSub = null;
    }
  }

  /**
   * Turn a transport-level failure into something an operator can act on.
   *
   * The API's own `message` is preferred when present. Without this, a service
   * that is simply not running surfaces Angular's raw
   * "Http failure response for <url>: 404 Not Found", which reads like a bug in
   * the page rather than an unavailable backend.
   */
  private describeVendorError(err: any, fallback: string): string {
    const error = err as { status?: number; error?: { message?: string } };
    const apiMessage = error?.error?.message;
    if (apiMessage) {
      return apiMessage;
    }

    switch (error?.status) {
      // 0 = network/CORS failure; 404 here means the route is absent, which in
      // practice means the service is not running the current build.
      case 0:
      case 404:
      case 502:
      case 503:
        return 'The vendor sync service is not reachable. Confirm svc-vendor-sync is running and up to date, then retry.';
      case 401:
        return 'Your session has expired. Sign in again to manage vendor credentials.';
      case 403:
        return 'Super admin access is required to manage vendor credentials.';
      default:
        return fallback;
    }
  }

  /**
   * Collect the credential form into the payload shape the API expects.
   *
   * Password-typed fields are sent verbatim; everything else is trimmed, since a
   * stray space in a location code or email is a typo but a stray space in a
   * password may be deliberate.
   */
  private buildCredentialPayload(): VendorCredentialInput {
    const payload: VendorCredentialInput = {};
    for (const field of this.activeCredentialFields) {
      const raw = this.credentialValues[field.key] ?? '';
      const value = field.type === 'password' ? raw : raw.trim();
      if (value) payload[field.key] = value;
    }
    return payload;
  }

  /** Verify the login without saving. Works even while storage is unavailable. */
  onTestCredentials(): void {
    const vendor = this.activeVendor;
    if (!vendor || !this.companyId || !this.hasCredentialInput || this.isTestingCredentials) {
      return;
    }
    const label = this.activeVendorLabel;
    this.isTestingCredentials = true;
    this.credentialError = '';
    this.credentialTestResult = null;

    this.vendorSyncService
      .testCredentials(vendor, this.companyId, this.buildCredentialPayload())
      .subscribe({
        next: response => {
          this.isTestingCredentials = false;
          this.credentialTestResult = response.data ?? null;
          this.messageService.add({
            severity: 'success',
            summary: 'Credentials verified',
            detail: this.describeTestResult(response.data ?? null, label),
          });
        },
        error: err => {
          this.isTestingCredentials = false;
          this.credentialError = this.describeVendorError(err, 'Credential verification failed');
        },
      });
  }

  /**
   * Human summary of a connection test.
   *
   * The member count is included when the adapter reports one, because a login
   * that succeeds against the wrong site looks identical to a correct one
   * otherwise — the count is what lets an operator catch it before saving.
   */
  private describeTestResult(result: VendorCredentialTestResult | null, label: string): string {
    if (!result?.location) return `${label} accepted these credentials.`;
    const where = `Connected to ${result.location}.`;
    return typeof result.memberCount === 'number'
      ? `${where} ${result.memberCount} active member${result.memberCount === 1 ? '' : 's'} found.`
      : where;
  }

  onSaveCredentials(): void {
    const vendor = this.activeVendor;
    if (!vendor || !this.companyId || !this.hasCredentialInput || this.isSavingCredentials) {
      return;
    }
    const label = this.activeVendorLabel;
    this.isSavingCredentials = true;
    this.credentialError = '';

    this.vendorSyncService
      .saveCredentials(vendor, this.companyId, this.buildCredentialPayload())
      .subscribe({
        next: () => {
          this.isSavingCredentials = false;
          // Do not keep the password in component state any longer than needed.
          this.clearCredentialInputs();
          this.messageService.add({
            severity: 'success',
            summary: 'Credentials saved',
            detail: `${label} credentials stored for this car wash.`,
          });
          this.loadCredentialStatus();
          // has_credentials flips true only once a credential row exists, unlocking
          // the plan-mapping save button — refetch so that shows up immediately.
          this.loadPlanMapping();
        },
        error: err => {
          this.isSavingCredentials = false;
          const status = err?.status;
          const message = this.describeVendorError(err, 'Failed to save credentials');
          // 501/409 mean the storage backend is unavailable, not that the
          // operator did something wrong — say so plainly.
          this.credentialError =
            status === 501 || status === 409 ? `${message} Credentials were not saved.` : message;
        },
      });
  }

  private clearCredentialInputs(): void {
    // Replaced wholesale rather than key-by-key so a field belonging to a vendor
    // the operator just switched away from cannot linger and be submitted.
    this.credentialValues = {};
    this.credentialTestResult = null;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.acceptFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.acceptFile(file);
    }
    input.value = '';
  }

  onRemoveFile(): void {
    this.selectedFile = null;
    this.fieldError = '';
  }

  onDownloadTemplate(): void {
    if (!this.companyId || this.isDownloadingTemplate) {
      return;
    }
    this.isDownloadingTemplate = true;
    this.importService.downloadTemplate(this.companyId).subscribe({
      next: blob => {
        this.isDownloadingTemplate = false;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'customers_migration_template.csv';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: err => {
        this.isDownloadingTemplate = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to download template',
        });
      },
    });
  }

  onSubmit(): void {
    if (!this.canSubmit || !this.companyId) {
      return;
    }

    this.fieldError = '';
    this.isSubmitting = true;

    // Vendor syncs run through svc-vendor-sync, which generates the CSV and
    // hands it to the same migration import. Different job type, separate path.
    if (this.isVendorMode) {
      this.submitVendorSync();
      return;
    }

    // Past the vendor early-return only the two migration sources remain, so
    // narrow to MigrationSourceType explicitly rather than casting sourceMode.
    const migrationSource: MigrationSourceType = this.sourceMode === 'csv' ? 'csv' : 'google_sheet';

    const request$ =
      migrationSource === 'csv'
        ? this.importService.createCsvJob(this.companyId, this.selectedFile!)
        : this.importService.createGoogleSheetJob(this.companyId, this.googleSheetUrl);

    request$.subscribe({
      next: response => {
        this.isSubmitting = false;
        if (response.status && response.data?.jobId) {
          this.activeJobId = response.data.jobId;
          this.jobStatus = 'queued';
          this.jobProgress = null;
          this.jobFailedReason = null;
          this.jobSourceType = migrationSource;
          this.messageService.add({
            severity: 'success',
            summary: 'Import started',
            detail: 'Customer migration job queued. Progress updates below.',
          });
          this.loadHistory();
          this.startPolling(response.data.jobId);
        } else {
          this.fieldError = response.message || 'Failed to start import';
        }
      },
      error: err => {
        this.isSubmitting = false;
        const existingJobId = err?.error?.data?.jobId;
        if (err?.status === 409 && existingJobId) {
          this.activeJobId = String(existingJobId);
          this.jobStatus = 'active';
          this.jobSourceType = migrationSource;
          this.fieldError = '';
          this.messageService.add({
            severity: 'warn',
            summary: 'Import already running',
            detail: 'Showing progress for the existing job.',
          });
          this.startPolling(String(existingJobId));
          return;
        }
        this.fieldError = err?.error?.message || err?.message || 'Failed to start import';
      },
    });
  }

  onResetAfterJob(): void {
    this.resetForm({ keepMode: true });
    if (this.isVendorMode) {
      this.loadCredentialStatus();
      this.loadPlanMapping();
      // Backs the redeem-location picker.
      this.loadShopLocations();
    }
  }

  private submitVendorSync(): void {
    const vendor = this.activeVendor;
    if (!vendor) {
      this.isSubmitting = false;
      return;
    }
    const label = this.activeVendorLabel;

    this.vendorSyncService.triggerSync(vendor, this.companyId).subscribe({
      next: response => {
        this.isSubmitting = false;
        const jobId = response.data?.jobId;
        if (!response.status || !jobId) {
          this.fieldError = response.message || `Failed to start ${label} sync`;
          return;
        }
        this.vendorJobId = jobId;
        this.vendorJobState = 'queued';
        this.vendorJobProgress = null;
        this.vendorJobFailedReason = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Sync started',
          detail: `Pulling members from ${label}. Progress updates below.`,
        });
        this.startVendorPolling(jobId);
      },
      error: err => {
        this.isSubmitting = false;
        this.fieldError = this.describeVendorError(err, `Failed to start ${label} sync`);
      },
    });
  }

  private startVendorPolling(jobId: string): void {
    this.stopVendorPolling();
    this.vendorPollSub = timer(0, 3000)
      .pipe(
        switchMap(() =>
          this.vendorSyncService.getSyncJob(jobId).pipe(
            catchError(err => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: err?.error?.message || 'Failed to poll sync progress',
              });
              return of(null);
            })
          )
        ),
        takeWhile(response => {
          if (!response?.data) {
            return true;
          }
          this.applyVendorProgress(response.data);
          return !VENDOR_TERMINAL_STATES.has(response.data.state);
        }, true)
      )
      .subscribe({
        complete: () => {
          this.vendorPollSub = null;
        },
      });
  }

  private applyVendorProgress(data: {
    state: string;
    progress: VendorSyncJobProgress | null;
    failedReason: string | null;
  }): void {
    this.vendorJobState = data.state;
    this.vendorJobProgress = data.progress || null;
    this.vendorJobFailedReason = data.failedReason || null;

    if (data.state === 'completed') {
      const inserted = data.progress?.inserted ?? 0;
      const sent = data.progress?.rowsSent ?? 0;
      const skipped = data.progress?.skipped ?? 0;
      this.messageService.add({
        severity: skipped > 0 ? 'warn' : 'success',
        summary: 'Sync complete',
        detail: skipped
          ? `${inserted} imported from ${sent} sent. ${skipped} members skipped — no email, phone or plate.`
          : `${inserted} customers imported from ${sent} rows.`,
      });
      this.loadHistory();
    } else if (data.state === 'failed') {
      this.messageService.add({
        severity: 'error',
        summary: 'Sync failed',
        detail: data.failedReason || 'Vendor sync job failed',
      });
      this.loadHistory();
    }
  }

  private stopVendorPolling(): void {
    if (this.vendorPollSub) {
      this.vendorPollSub.unsubscribe();
      this.vendorPollSub = null;
    }
  }

  private stopCredentialLoad(): void {
    if (this.credentialSub) {
      this.credentialSub.unsubscribe();
      this.credentialSub = null;
    }
  }

  private acceptFile(file: File): void {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.csv') && file.type !== 'text/csv') {
      this.fieldError = 'Only CSV files are allowed';
      this.selectedFile = null;
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      this.fieldError = 'CSV must be 25MB or smaller';
      this.selectedFile = null;
      return;
    }
    this.fieldError = '';
    this.selectedFile = file;
  }

  private startPolling(jobId: string): void {
    this.stopPolling();
    const companyId = this.companyId;
    this.pollSub = timer(0, 2000)
      .pipe(
        switchMap(() =>
          this.importService.getJobProgress(companyId, jobId).pipe(
            catchError(err => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: err?.error?.message || 'Failed to poll import progress',
              });
              return of(null);
            })
          )
        ),
        takeWhile(response => {
          if (!response?.data) {
            return true;
          }
          this.applyProgress(response.data);
          return !TERMINAL_STATUSES.has(response.data.status);
        }, true)
      )
      .subscribe({
        complete: () => {
          this.pollSub = null;
        },
      });
  }

  private applyProgress(data: MigrationJobProgressResponse): void {
    this.jobStatus = data.status;
    this.jobProgress = data.progress || null;
    this.jobFailedReason = data.failedReason || null;
    this.jobSourceType = data.sourceType || this.jobSourceType;

    if (data.status === 'completed') {
      const inserted = data.progress?.inserted ?? 0;
      const updated = data.progress?.updated ?? data.progress?.duplicates ?? 0;
      this.messageService.add({
        severity: 'success',
        summary: 'Import complete',
        detail: `${inserted} customers imported${updated ? `, ${updated} existing customers updated` : ''}.`,
      });
      this.loadHistory();
    } else if (data.status === 'failed') {
      this.messageService.add({
        severity: 'error',
        summary: 'Import failed',
        detail: data.failedReason || 'Migration job failed',
      });
      this.loadHistory();
    }
  }

  private stopPolling(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
  }

  private stopHistoryLoad(): void {
    if (this.historySub) {
      this.historySub.unsubscribe();
      this.historySub = null;
    }
  }

  private resetForm(options?: { keepMode?: boolean }): void {
    this.stopPolling();
    this.stopVendorPolling();
    if (!options?.keepMode) {
      this.sourceMode = 'csv';
    }
    this.selectedFile = null;
    this.isDragOver = false;
    this.googleSheetUrl = '';
    this.fieldError = '';
    this.isSubmitting = false;
    this.activeJobId = null;
    this.jobStatus = null;
    this.jobProgress = null;
    this.jobFailedReason = null;
    this.jobSourceType = null;

    // Vendor sync state. Credential inputs are cleared so a password never
    // survives a company switch or a completed run in component memory.
    this.clearCredentialInputs();
    this.showCredentialForm = false;
    this.credentialError = '';
    this.isTestingCredentials = false;
    this.isSavingCredentials = false;
    this.vendorJobId = null;
    this.vendorJobState = null;
    this.vendorJobProgress = null;
    this.vendorJobFailedReason = null;

    // Plan mapping itself is persistent per-shop config (like credentialStatus above)
    // and is left alone; only this draft-editing state is transient.
    this.planMappingError = '';
    this.planMappingSaved = false;
    this.newPlanName = '';
    this.newPackageId = '';
  }

  /**
   * Loads page 1 by default, not the page currently open.
   *
   * Every no-argument caller means "something changed, show me the newest": init,
   * a company switch, an import finishing, a sync completing, history cleared. A
   * new run lands on page 1, so reusing the open page hid the very thing that
   * triggered the reload — and on a company switch it asked the new company for a
   * page it may not have, showing an empty history as if nothing was imported.
   * The server does not rescue this: a page past the end returns an empty array
   * and echoes the page back, so there is nothing to self-correct from.
   * Only the pager passes a page explicitly.
   */
  private loadHistory(page = 1): void {
    this.stopHistoryLoad();
    if (!this.companyId) {
      this.history = [];
      this.historyPagination = null;
      this.historyLoading = false;
      return;
    }
    const companyId = this.companyId;
    this.historyLoading = true;
    this.historySub = this.importService.getHistory(companyId, HISTORY_PAGE_SIZE, page).subscribe({
      next: response => {
        if (companyId !== this.companyId) {
          return;
        }
        this.historyLoading = false;
        this.historySub = null;
        if (response.status && Array.isArray(response.data)) {
          this.history = response.data;
          this.historyPagination = response.pagination ?? null;
          // Trust the server's answer over the requested page: a run cleared or
          // added between renders can leave the asked-for page past the end.
          this.historyPage = response.pagination?.current_page ?? page;
        } else {
          this.history = [];
          this.historyPagination = null;
        }
      },
      error: () => {
        if (companyId !== this.companyId) {
          return;
        }
        this.historyLoading = false;
        this.historySub = null;
        this.history = [];
        this.historyPagination = null;
      },
    });
  }

  get historyTotalPages(): number {
    return this.historyPagination?.total_pages ?? 0;
  }

  get canGoPrevHistoryPage(): boolean {
    return this.historyPage > 1 && !this.historyLoading;
  }

  get canGoNextHistoryPage(): boolean {
    return this.historyPage < this.historyTotalPages && !this.historyLoading;
  }

  /** Range label, e.g. "11–20 of 137". Reads from the server's totals, not the row count. */
  get historyRangeLabel(): string {
    const total = this.historyPagination?.total_records ?? 0;
    if (!total) return '';
    const perPage = this.historyPagination?.per_page ?? HISTORY_PAGE_SIZE;
    const first = (this.historyPage - 1) * perPage + 1;
    const last = Math.min(total, first + this.history.length - 1);
    return `${first}\u2013${last} of ${total}`;
  }

  goToHistoryPage(page: number): void {
    const target = Math.max(1, page);
    if (this.historyLoading || target === this.historyPage) return;
    if (this.historyTotalPages && target > this.historyTotalPages) return;
    this.loadHistory(target);
  }

  onClearHistoryClick(): void {
    if (!this.companyId || !this.history.length || this.isClearingHistory || this.isJobRunning) {
      return;
    }
    this.showClearHistoryConfirmation = true;
  }

  onCancelClearHistory(): void {
    if (this.isClearingHistory) {
      return;
    }
    this.showClearHistoryConfirmation = false;
  }

  onConfirmClearHistory(): void {
    this.historyPage = 1;
    if (!this.companyId || this.isClearingHistory) {
      return;
    }
    this.isClearingHistory = true;
    this.clearHistoryConfirmationData = {
      ...CLEAR_HISTORY_CONFIRMATION,
      isLoading: true,
    };
    this.importService.clearHistory(this.companyId).subscribe({
      next: response => {
        this.isClearingHistory = false;
        this.showClearHistoryConfirmation = false;
        this.clearHistoryConfirmationData = { ...CLEAR_HISTORY_CONFIRMATION };
        if (!response.status) {
          this.messageService.add({
            severity: 'error',
            summary: 'Clear failed',
            detail: response.message || 'Failed to clear import history',
          });
          return;
        }
        const deleted = response.data?.deleted ?? 0;
        this.messageService.add({
          severity: 'success',
          summary: 'History cleared',
          detail: deleted
            ? `Removed ${deleted} recent import${deleted === 1 ? '' : 's'}.`
            : 'No completed imports to clear.',
        });
        this.loadHistory();
      },
      error: err => {
        this.isClearingHistory = false;
        this.showClearHistoryConfirmation = false;
        this.clearHistoryConfirmationData = { ...CLEAR_HISTORY_CONFIRMATION };
        this.messageService.add({
          severity: 'error',
          summary: 'Clear failed',
          detail: err?.error?.message || err?.message || 'Failed to clear import history',
        });
      },
    });
  }
}
